import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, NavigateFunction } from "react-router-dom";
import {
  Compass,
  Target,
  ChevronRight,
  ChevronDown,
  BookOpen,
  FileSpreadsheet,
  ListChecks,
  AlertCircle,
  GraduationCap,
  CheckCircle2,
  Circle,
} from "lucide-react";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { ApiService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import DiagnosticRunner from "./DiagnosticRunner";
import TeachFlow from "./TeachFlow";
import LessonWalk from "./LessonWalk";
import Defterim from "./Defterim";
import type {
  KonuState,
  NavigatorKonu,
  NavigatorLessonStatus,
  NavigatorStatusResponse,
  LessonWalkKazanim,
  LessonWalkResponse,
} from "../../types/navigator";

// Konu durumu görsel sözlüğü — projedeki chip standardı: bg-{c}-50 / text-{c}-700 / border-{c}-200.
const STATE_META: Record<
  KonuState,
  { label: string; bar: string; chip: string; dot: string }
> = {
  strong: {
    label: "Güçlü",
    bar: "bg-emerald-500",
    chip: "border-emerald-200 bg-emerald-50 text-emerald-700",
    dot: "bg-emerald-500",
  },
  developing: {
    label: "Gelişiyor",
    bar: "bg-amber-500",
    chip: "border-amber-200 bg-amber-50 text-amber-700",
    dot: "bg-amber-500",
  },
  weak: {
    label: "Zayıf",
    bar: "bg-rose-500",
    chip: "border-rose-200 bg-rose-50 text-rose-700",
    dot: "bg-rose-500",
  },
  insufficient: {
    label: "Az veri",
    bar: "bg-slate-400",
    chip: "border-slate-200 bg-slate-50 text-slate-600",
    dot: "bg-slate-400",
  },
  untested: {
    label: "Henüz test edilmedi",
    bar: "bg-gray-300",
    chip: "border-dashed border-gray-300 bg-gray-50 text-gray-500",
    dot: "bg-gray-300",
  },
};

const STATE_ORDER: KonuState[] = ["weak", "developing", "insufficient", "strong", "untested"];

function KonuCard({
  konu,
  onPractice,
  onTeach,
  expanded,
  kazanimlar,
  loading,
  onToggle,
}: {
  konu: NavigatorKonu;
  onPractice: () => void;
  onTeach: () => void;
  expanded: boolean;
  kazanimlar: LessonWalkKazanim[] | null;
  loading: boolean;
  onToggle: () => void;
}) {
  const meta = STATE_META[konu.state];
  const hasKazanim = konu.kazanimTotal > 0;

  return (
    <div className="flex flex-col rounded-lg border border-gray-200 bg-white p-4 transition-shadow hover:shadow-md">
      <div className="mb-2 flex items-start justify-between gap-2">
        <span className="text-sm font-medium text-gray-900 line-clamp-2">{konu.konu}</span>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${meta.chip}`}>
          {meta.label}
        </span>
      </div>

      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
        {konu.mastery !== null ? (
          <div className={`h-full rounded-full ${meta.bar}`} style={{ width: `${konu.mastery}%` }} />
        ) : (
          <div className="h-full w-full bg-[repeating-linear-gradient(45deg,#e5e7eb_0_6px,transparent_6px_12px)]" />
        )}
      </div>

      <div className="mt-1.5 flex items-center justify-between text-[11px] text-gray-400">
        <span>{konu.mastery !== null ? `%${konu.mastery} hakimiyet` : "veri yok"}</span>
        <span>{konu.sampleSize > 0 ? `${konu.sampleSize} soru` : "—"}</span>
      </div>

      {/* Kazanım ilerleme rozeti — geçtiğin kazanımlar görünür */}
      {hasKazanim && (
        <button
          onClick={onToggle}
          className="mt-2 flex items-center gap-1.5 rounded-md text-[11px] font-medium text-gray-600 transition-colors hover:text-blue-700"
        >
          <CheckCircle2 className={`h-3.5 w-3.5 ${konu.kazanimMastered > 0 ? "text-emerald-500" : "text-gray-300"}`} />
          <span className="font-semibold text-gray-700">
            {konu.kazanimMastered}/{konu.kazanimTotal}
          </span>
          <span>kazanım pekişti</span>
          {konu.kazanimTaught > konu.kazanimMastered && (
            <span className="text-amber-600">· {konu.kazanimTaught} anlatıldı</span>
          )}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      )}

      {/* Kazanım checklist'i (açılınca) */}
      {expanded && (
        <div className="mt-2 rounded-lg border border-gray-100 bg-gray-50/70 p-2">
          {loading || !kazanimlar ? (
            <div className="grid place-items-center py-3">
              <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="space-y-0.5">
              {kazanimlar.map((k) => {
                const st = k.mastered ? "mastered" : k.taught ? "taught" : "pending";
                return (
                  <div key={k.gainId} className="flex items-center gap-2 px-1 py-1 text-[11px]">
                    {st === "mastered" ? (
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                    ) : st === "taught" ? (
                      <Circle className="h-3.5 w-3.5 shrink-0 fill-amber-100 text-amber-500" />
                    ) : (
                      <Circle className="h-3.5 w-3.5 shrink-0 text-gray-300" />
                    )}
                    <span className={`min-w-0 flex-1 truncate ${st === "mastered" ? "text-gray-400 line-through" : "text-gray-700"}`}>
                      {k.gainText}
                    </span>
                    {st === "mastered" && <span className="shrink-0 text-[10px] font-medium text-emerald-600">pekişti</span>}
                    {st === "taught" && <span className="shrink-0 text-[10px] font-medium text-amber-600">anlatıldı</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="mt-2.5 flex items-center gap-3">
        <button
          onClick={onTeach}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 transition-colors hover:text-blue-700"
        >
          <GraduationCap className="h-3.5 w-3.5" /> Bana anlat
        </button>
        <span className="text-gray-200">·</span>
        <button
          onClick={onPractice}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-600 transition-colors hover:text-blue-700"
        >
          Test çöz <ChevronRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function QuickActions({ navigate, onPractice, lessonId }: { navigate: NavigateFunction; onPractice: () => void; lessonId: string }) {
  const items = [
    { icon: FileSpreadsheet, label: "Deneme yap", action: onPractice },
    { icon: BookOpen, label: "Konu seç", action: () => navigate(`/lessons/play/${lessonId}`) },
    { icon: ListChecks, label: "Eksiklerim", action: () => navigate("/lessons?tab=deficiencies") },
  ];
  return (
    <div className="grid grid-cols-3 gap-3">
      {items.map(({ icon: Icon, label, action }) => (
        <button
          key={label}
          onClick={action}
          className="flex flex-col items-center gap-1.5 rounded-lg border border-gray-200 bg-white p-3 text-center transition-colors hover:border-blue-300 hover:bg-gray-50"
        >
          <Icon className="h-5 w-5 text-blue-600" />
          <span className="text-xs font-medium text-gray-700">{label}</span>
        </button>
      ))}
    </div>
  );
}

function CoachStrip({
  firstName,
  coachLine,
  onStartWalk,
}: {
  firstName: string;
  coachLine: string;
  onStartWalk: () => void;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-blue-100 text-blue-600">
          <Compass className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-gray-400">Merhaba {firstName} 👋</p>
          <p className="text-base font-semibold leading-snug text-gray-900">{coachLine}</p>
        </div>
      </div>

      {/* Bana dersi anlat — kazanım kazanım hiyerarşik yürüyüş */}
      <button
        onClick={onStartWalk}
        className="mt-3 flex w-full items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
      >
        <GraduationCap className="h-4 w-4 shrink-0" />
        Bana dersi anlat
        <ChevronRight className="ml-auto h-4 w-4 shrink-0" />
      </button>
    </div>
  );
}

// Tek ders için tam görünüm: koç şeridi + hızlı aksiyon + durum panosu.
interface PracticeScope {
  sectionId?: string;
  count?: number;
  scopeLabel?: string;
}

function LessonView({
  lesson,
  firstName,
  navigate,
  onStartDiagnostic,
  onStartPractice,
  onStartTeach,
  onStartWalk,
}: {
  lesson: NavigatorLessonStatus;
  firstName: string;
  navigate: NavigateFunction;
  onStartDiagnostic: (lessonId: string) => void;
  onStartPractice: (lessonId: string, scope: PracticeScope) => void;
  onStartTeach: (lessonId: string, sectionId: string) => void;
  onStartWalk: (lessonId: string) => void;
}) {
  const coachLine = useMemo(() => {
    if (lesson.overallMastery === null) {
      return `Hadi başlayalım! Kısa bir denemeyle ${lesson.title} seviyeni ölçüp sana özel yol haritanı çıkarayım.`;
    }
    if (lesson.nextFocus) {
      return `En çok ${lesson.nextFocus.konu} (%${lesson.nextFocus.mastery}) seni zorluyor. Bugün burada çalışalım mı?`;
    }
    if (lesson.firstUntested) {
      return `İyi gidiyorsun! Henüz denemediğin “${lesson.firstUntested.konu}” konusuna göz atalım mı?`;
    }
    return "Bugün nereden devam edelim? İstediğin konuyu seç ya da bir deneme çöz.";
  }, [lesson]);

  const hasData = lesson.overallMastery !== null;

  // Kazanım checklist'i: ilk açılışta getLessonWalk çekilir (lazy), bölüme göre gruplanır
  const [walk, setWalk] = useState<LessonWalkResponse | null>(null);
  const [walkLoading, setWalkLoading] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const kazBySection = useMemo(() => {
    const m = new Map<string, LessonWalkKazanim[]>();
    (walk?.kazanimlar || []).forEach((k) => {
      if (!m.has(k.sectionId)) m.set(k.sectionId, []);
      m.get(k.sectionId)!.push(k);
    });
    return m;
  }, [walk]);

  const toggleExpand = async (sectionId: string) => {
    const next = expandedSection === sectionId ? null : sectionId;
    setExpandedSection(next);
    if (next && !walk && !walkLoading) {
      setWalkLoading(true);
      try {
        setWalk(await ApiService.getLessonWalk(lesson.lessonId));
      } catch {
        /* geç */
      } finally {
        setWalkLoading(false);
      }
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5 px-4 py-5 sm:px-6">
      <CoachStrip firstName={firstName} coachLine={coachLine} onStartWalk={() => onStartWalk(lesson.lessonId)} />
      <QuickActions
        navigate={navigate}
        lessonId={lesson.lessonId}
        onPractice={() => onStartPractice(lesson.lessonId, { scopeLabel: "Tüm ders" })}
      />

      <div className="flex items-center gap-2">
        <h2 className="text-base font-semibold text-gray-900">Durumun</h2>
        <span className="text-sm text-gray-400">konu konu neredesin</span>
      </div>

      {!hasData ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-lg bg-blue-50 text-blue-600">
            <Compass className="h-6 w-6" />
          </div>
          <p className="mb-1 text-base font-semibold text-gray-900">Bu derste haritan henüz boş</p>
          <p className="mx-auto mb-4 max-w-md text-sm text-gray-600">
            Kısa bir seviye tespitiyle başlayalım; ~20 soruda hangi konularda güçlü, nerede eksik
            olduğunu birlikte görelim ve sana özel bir yol çıkaralım.
          </p>
          <button
            onClick={() => onStartDiagnostic(lesson.lessonId)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            Seviyeni ölç <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <>
          {/* Tanı daveti (veri var ama tanı yapılmamışsa) */}
          {!lesson.diagnosticDone && (
            <button
              onClick={() => onStartDiagnostic(lesson.lessonId)}
              className="flex w-full items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-left transition-colors hover:bg-blue-100/60"
            >
              <Compass className="h-5 w-5 shrink-0 text-blue-600" />
              <div className="min-w-0">
                <p className="text-xs font-medium text-blue-700">Seviye tespiti</p>
                <p className="text-sm font-semibold text-gray-900">Kısa bir tanıyla haritanı hızlıca netleştir</p>
              </div>
              <ChevronRight className="ml-auto h-5 w-5 shrink-0 text-blue-400" />
            </button>
          )}

          {/* Ders özeti */}
          <div className="flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="grid h-14 w-14 place-items-center rounded-lg bg-blue-50 text-blue-600">
                <span className="text-lg font-bold">%{lesson.overallMastery}</span>
              </div>
              <div>
                <p className="text-xs text-gray-400">Genel hakimiyet</p>
                <p className="text-sm font-semibold text-gray-900">{lesson.title}</p>
              </div>
            </div>
            <div className="ml-auto flex flex-wrap gap-3 text-xs">
              {STATE_ORDER.map((s) => (
                <div key={s} className="flex items-center gap-1.5">
                  <span className={`h-2.5 w-2.5 rounded-full ${STATE_META[s].dot}`} />
                  <span className="text-gray-500">{STATE_META[s].label}</span>
                  <span className="font-semibold text-gray-700">{lesson.counts[s] ?? 0}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bugün odak kartı */}
          {lesson.nextFocus && (
            <button
              onClick={() => navigate(`/lessons/detail/${lesson.lessonId}`)}
              className="flex w-full items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-left transition-colors hover:bg-rose-100/70"
            >
              <Target className="h-5 w-5 shrink-0 text-rose-500" />
              <div className="min-w-0">
                <p className="text-xs font-medium text-rose-600">Bugün buna odaklan</p>
                <p className="truncate text-sm font-semibold text-gray-900">
                  {lesson.nextFocus.konu}{" "}
                  <span className="font-normal text-gray-500">
                    · %{lesson.nextFocus.mastery} · {lesson.nextFocus.unit}
                  </span>
                </p>
              </div>
              <ChevronRight className="ml-auto h-5 w-5 shrink-0 text-rose-400" />
            </button>
          )}

          {/* Üniteler → konu kartları */}
          {lesson.units.map((unit) => (
            <div key={unit.unit}>
              <div className="mb-2 flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900">{unit.unit}</h3>
                <span className="text-xs text-gray-400">{unit.konular.length} konu</span>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {unit.konular.map((konu) => (
                  <KonuCard
                    key={konu.sectionId}
                    konu={konu}
                    expanded={expandedSection === konu.sectionId}
                    kazanimlar={kazBySection.get(konu.sectionId) || null}
                    loading={walkLoading && expandedSection === konu.sectionId}
                    onToggle={() => toggleExpand(konu.sectionId)}
                    onPractice={() =>
                      onStartPractice(lesson.lessonId, {
                        sectionId: konu.sectionId,
                        count: 10,
                        scopeLabel: konu.konu,
                      })
                    }
                    onTeach={() => onStartTeach(lesson.lessonId, konu.sectionId)}
                  />
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export default function Navigator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<NavigatorStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runner, setRunner] = useState<{ lessonId: string; practice?: PracticeScope } | null>(null);
  const [teach, setTeach] = useState<{ lessonId: string; sectionId: string } | null>(null);
  const [walkLessonId, setWalkLessonId] = useState<string | null>(null);

  const firstName = useMemo(() => {
    const name = user?.name || user?.firstName || "";
    return String(name).split(" ")[0] || "öğrenci";
  }, [user]);

  const fetchStatus = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await ApiService.getNavigatorStatus();
      setData(res);
    } catch (e: any) {
      setError(e?.message || "Durum yüklenemedi");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const tabs: TabItemProps[] = useMemo(() => {
    const lessons = data?.lessons || [];
    const lessonTabs: TabItemProps[] = lessons.map((lesson) => ({
      type: "custom" as const,
      title: lesson.title,
      path: `navigator-${lesson.lessonId}`,
      tabId: lesson.lessonId,
      customComponent: (
        <LessonView
          lesson={lesson}
          firstName={firstName}
          navigate={navigate}
          onStartDiagnostic={(lessonId) => setRunner({ lessonId })}
          onStartPractice={(lessonId, practice) => setRunner({ lessonId, practice })}
          onStartTeach={(lessonId, sectionId) => setTeach({ lessonId, sectionId })}
          onStartWalk={(lessonId) => setWalkLessonId(lessonId)}
        />
      ),
    }));
    if (!lessons.length) return lessonTabs;
    // Defterim sekmesi — tüm derslerin ilerleme özeti + "neler yaptın" günlüğü
    lessonTabs.push({
      type: "custom" as const,
      title: "Defterim",
      path: "navigator-defterim",
      tabId: "defterim",
      customComponent: <Defterim lessons={lessons} />,
    });
    return lessonTabs;
  }, [data, firstName, navigate]);

  if (loading) {
    return (
      <div className="grid min-h-[calc(100dvh-4rem)] place-items-center bg-gray-50">
        <div className="h-9 w-9 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[calc(100dvh-4rem)] bg-gray-50 p-4 sm:p-6">
        <div className="mx-auto flex max-w-5xl items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      </div>
    );
  }

  if (!data || tabs.length === 0) {
    return (
      <div className="grid min-h-[calc(100dvh-4rem)] place-items-center bg-gray-50 p-6">
        <div className="max-w-md rounded-lg border border-gray-200 bg-white p-8 text-center">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-lg bg-blue-50 text-blue-600">
            <Compass className="h-6 w-6" />
          </div>
          <p className="mb-1 text-base font-semibold text-gray-900">Henüz bir dersin yok</p>
          <p className="text-sm text-gray-600">
            Ders erişimin açıldığında koçun burada seni karşılayıp yol haritanı çıkaracak.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <TabPage title="Koçum" tabs={tabs} />
      {runner && (
        <DiagnosticRunner
          lessonId={runner.lessonId}
          practice={runner.practice}
          onClose={() => setRunner(null)}
          onComplete={() => fetchStatus(true)}
        />
      )}
      {teach && (
        <TeachFlow
          sectionId={teach.sectionId}
          onClose={() => setTeach(null)}
          onStartTest={(testScope, konu) => {
            const lessonId = teach.lessonId;
            setTeach(null);
            setRunner({
              lessonId,
              practice: {
                sectionId: testScope.sectionId || undefined,
                count: testScope.count,
                scopeLabel: konu,
              },
            });
          }}
        />
      )}
      {walkLessonId && (
        <LessonWalk
          lessonId={walkLessonId}
          onClose={() => setWalkLessonId(null)}
          onStartTest={(testScope, konu) => {
            const lessonId = walkLessonId;
            setWalkLessonId(null);
            setRunner({
              lessonId,
              practice: {
                sectionId: testScope.sectionId || undefined,
                count: testScope.count,
                scopeLabel: konu,
              },
            });
          }}
        />
      )}
    </>
  );
}
